import { AlertTriangle, Crown, UserPlus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface NoLeaderWarningProps {
  hasCandidates: boolean
  onGoToCandidates: () => void
  onGoToRecruitment: () => void
  className?: string
}

export function NoLeaderWarning({
  hasCandidates,
  onGoToCandidates,
  onGoToRecruitment,
  className,
}: NoLeaderWarningProps) {
  return (
    <Card
      className={cn(
        'border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 to-transparent',
        className
      )}
    >
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center space-y-4">
          {/* Icon */}
          <div className="relative">
            <div className="rounded-full bg-amber-500/20 p-4">
              <Crown className="h-8 w-8 text-amber-500" />
            </div>
            <div className="absolute -top-1 -right-1 bg-amber-500 rounded-full p-1">
              <AlertTriangle className="h-4 w-4 text-amber-950" />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1">
            <h3 className="font-semibold text-lg text-amber-700 dark:text-amber-400">
              No Party Leader Assigned
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Your party needs a leader. The party leader is the face of your party and
              heavily influences voter perception and election outcomes.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            {hasCandidates ? (
              <Button
                variant="default"
                className="gap-2 bg-amber-500 hover:bg-amber-600 text-amber-950"
                onClick={onGoToCandidates}
              >
                <Crown className="h-4 w-4" />
                Assign a Leader
              </Button>
            ) : (
              <Button
                variant="default"
                className="gap-2 bg-amber-500 hover:bg-amber-600 text-amber-950"
                onClick={onGoToRecruitment}
              >
                <UserPlus className="h-4 w-4" />
                Recruit Your First Candidate
              </Button>
            )}
          </div>

          {/* Additional hint */}
          <p className="text-xs text-muted-foreground">
            {hasCandidates
              ? 'Select a candidate and assign them the Party Leader role.'
              : 'Recruit candidates from the Recruitment section below.'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
